import type { MedplumClient } from '@medplum/core';
import { getZoomSignature } from './signature';

export type ZoomClient = typeof import('@zoom/meetingsdk').ZoomMtg;

export interface ZoomJoinConfig {
  meetingNumber: string;
  password?: string;
  userName: string;
  userEmail?: string;
  role?: number;
  leaveUrl?: string;
  /** Hide SDK chrome for in-person recording (host popup stays visually minimized). */
  hiddenUi?: boolean;
}

function showClientViewRoot(hidden: boolean): void {
  const root = document.getElementById('zmmtg-root');
  if (!root) return;
  root.style.display = 'block';
  if (hidden) {
    root.style.position = 'fixed';
    root.style.width = '1px';
    root.style.height = '1px';
    root.style.overflow = 'hidden';
    root.style.opacity = '0';
    root.style.pointerEvents = 'none';
    root.style.left = '-9999px';
    root.style.top = '-9999px';
  }
}

export function hideClientViewRoot(): void {
  const root = document.getElementById('zmmtg-root');
  if (root) {
    root.style.display = 'none';
  }
}

function initZoomClient(ZoomMtg: ZoomClient, leaveUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    ZoomMtg.init({
      leaveUrl,
      patchJsMedia: true,
      disableInvite: true,
      disableCallOut: true,
      disableRecord: true,
      disablePreview: true,
      disableZoomLogo: true,
      disablePictureInPicture: true,
      isSupportChat: false,
      isSupportQA: false,
      isSupportPolling: false,
      isSupportBreakout: false,
      meetingInfo: [],
      leaveOnPageUnload: true,
      success: () => resolve(),
      error: (err: unknown) => reject(err),
    });
  });
}

function joinZoomClient(
  ZoomMtg: ZoomClient,
  cfg: ZoomJoinConfig,
  signature: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    ZoomMtg.join({
      signature,
      meetingNumber: cfg.meetingNumber,
      passWord: cfg.password ?? '',
      userName: cfg.userName,
      userEmail: cfg.userEmail,
      success: () => resolve(),
      error: (err: unknown) => reject(err),
    });
  });
}

export async function joinZoomMeeting(
  medplum: MedplumClient,
  cfg: ZoomJoinConfig,
): Promise<ZoomClient> {
  const { ZoomMtg } = await import('@zoom/meetingsdk');
  ZoomMtg.preLoadWasm();
  ZoomMtg.prepareWebSDK();
  showClientViewRoot(cfg.hiddenUi === true);

  const signature = await getZoomSignature(medplum, cfg.meetingNumber, cfg.role ?? 1);
  const leaveUrl = cfg.leaveUrl ?? window.location.href;
  await initZoomClient(ZoomMtg, leaveUrl);
  await joinZoomClient(ZoomMtg, cfg, signature);
  return ZoomMtg;
}

export async function leaveZoomMeeting(ZoomMtg: ZoomClient): Promise<void> {
  return new Promise((resolve) => {
    ZoomMtg.leaveMeeting({
      confirm: false,
      success: () => resolve(),
      error: () => resolve(),
    });
  });
}

export async function setZoomMicMuted(ZoomMtg: ZoomClient, muted: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    ZoomMtg.getCurrentUser({
      success: (res: { result?: { currentUser?: { userId?: number } } }) => {
        const userId = res.result?.currentUser?.userId;
        if (userId == null) {
          reject(new Error('Could not resolve Zoom participant'));
          return;
        }
        if (muted) {
          ZoomMtg.mute({
            userId,
            mute: true,
            success: () => resolve(),
            error: (err: unknown) => reject(err),
          });
          return;
        }
        ZoomMtg.mute({
          userId,
          mute: false,
          success: () => resolve(),
          error: (err: unknown) => reject(err),
        });
      },
      error: (err: unknown) => reject(err),
    });
  });
}

export type ZoomAudioInputDevice = {
  deviceId: string;
  label: string;
};

/** List microphones available to the host popup (requires prior mic permission). */
export async function listZoomAudioInputDevices(): Promise<ZoomAudioInputDevice[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === 'audioinput' && Boolean(d.deviceId))
    .map((d, index) => ({
      deviceId: d.deviceId,
      label: d.label?.trim() || `Microphone ${index + 1}`,
    }));
}

/**
 * Prefer a microphone for this browser tab. Meeting SDK Client View has no public
 * switchMicrophone API, so we nudge the preferred capture device and best-effort
 * retarget any live audio tracks already on the page.
 */
export async function preferZoomAudioInputDevice(deviceId: string): Promise<void> {
  const id = deviceId.trim();
  if (!id || !navigator.mediaDevices?.getUserMedia) return;

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { deviceId: { exact: id } },
  });
  for (const track of stream.getAudioTracks()) {
    track.stop();
  }

  for (const el of document.querySelectorAll('audio, video')) {
    const media = el as HTMLMediaElement;
    const current = media.srcObject;
    if (!(current instanceof MediaStream)) continue;
    for (const track of current.getAudioTracks()) {
      try {
        await track.applyConstraints({ deviceId: { exact: id } });
      } catch {
        // Client View may own tracks that reject deviceId constraints.
      }
    }
  }
}
